import { useState } from "react";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { PHONE, EMAIL } from "@/mocks/updiag";

export default function ContactPage() {
  const [formData, setFormData] = useState({ nom: "", email: "", telephone: "", sujet: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) { setError("Le message ne doit pas dépasser 500 caractères."); return; }
    setError("");
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch("https://readdy.ai/api/form/d7evipnimhg6tri2v0cg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <title>Contact Up Diag — Diagnostic Immobilier Paris & IDF | 01 XX XX XX XX</title>
      <meta name="description" content="Contactez Up Diag pour vos diagnostics immobiliers à Paris et en Île-de-France. Téléphone, email, formulaire. Réponse sous 2h, intervention sous 48h." />
      <TopBar />
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F3B5F] to-[#0a2d4a] py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Contactez-nous
            </h1>
            <p className="text-lg text-white/80">Notre équipe vous répond sous 2h — Lun–Sam 8h–19h</p>
          </div>
        </section>

        {/* Contact cards */}
        <section className="bg-white py-14">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-14">
              {[
                { icon: "ri-phone-fill", title: "Téléphone", value: PHONE, sub: "Lun–Sam 8h–19h", href: `tel:${PHONE.replace(/\s/g, "")}`, color: "text-[#F97316]" },
                { icon: "ri-mail-fill", title: "Email", value: EMAIL, sub: "Réponse sous 2h", href: `mailto:${EMAIL}`, color: "text-[#0F3B5F]" },
                { icon: "ri-map-pin-2-fill", title: "Zone d'intervention", value: "Paris & Île-de-France", sub: "8 départements couverts", href: "/zones", color: "text-[#16A34A]" },
              ].map((item) => (
                <a key={item.title} href={item.href} className="group bg-[#F8FAFC] rounded-2xl p-7 border border-gray-100 hover:border-orange-200 hover:-translate-y-1 transition-all duration-300 text-center block">
                  <div className="w-14 h-14 flex items-center justify-center bg-white rounded-xl mx-auto mb-4 border border-gray-100">
                    <i className={`${item.icon} ${item.color} text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#0F3B5F] mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>{item.title}</h3>
                  <p className="font-semibold text-[#0F3B5F] text-sm mb-1">{item.value}</p>
                  <p className="text-xs text-gray-400">{item.sub}</p>
                </a>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Form */}
              <div>
                <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-6" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Envoyez-nous un message
                </h2>
                {submitted ? (
                  <div className="bg-green-50 rounded-2xl p-8 text-center border border-green-100">
                    <i className="ri-checkbox-circle-fill text-[#16A34A] text-4xl mb-3 block"></i>
                    <h3 className="font-bold text-[#0F3B5F] text-lg mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>Message envoyé !</h3>
                    <p className="text-gray-500 text-sm">Nous vous répondons sous 2h.</p>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Nom complet *</label>
                        <input type="text" name="nom" placeholder="Jean Dupont" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                          className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Téléphone</label>
                        <input type="tel" name="telephone" placeholder="06 XX XX XX XX" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                          className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Email *</label>
                      <input type="email" name="email" placeholder="jean@exemple.fr" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors" required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Sujet</label>
                      <select name="sujet" value={formData.sujet} onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                        className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors">
                        <option value="">Sélectionner un sujet...</option>
                        <option value="devis">Demande de devis</option>
                        <option value="rdv">Prise de rendez-vous</option>
                        <option value="info">Renseignements</option>
                        <option value="rapport">Question sur un rapport</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0F3B5F] mb-1.5">Message *</label>
                      <textarea name="message" placeholder="Décrivez votre besoin..." value={formData.message}
                        onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }}
                        rows={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#F97316] focus:outline-none transition-colors resize-none" required />
                      <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button type="submit" disabled={submitting || !formData.nom || !formData.email || !formData.message}
                      className="w-full py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all disabled:opacity-50 whitespace-nowrap">
                      {submitting ? "Envoi..." : "Envoyer le message"}
                      {!submitting && <i className="ri-send-plane-fill ml-2"></i>}
                    </button>
                    <p className="text-xs text-gray-400 text-center">Réponse garantie sous 2h — Lun–Sam 8h–19h</p>
                  </form>
                )}
              </div>

              {/* Map + Infos */}
              <div className="flex flex-col gap-5">
                <div className="rounded-2xl overflow-hidden border border-gray-100 h-72">
                  <iframe
                    title="Up Diag — Paris & Île-de-France"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d167998.55754999!2d2.2769952!3d48.8588897!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e1f06e2b70f%3A0x40b82c3688c9460!2sParis!5e0!3m2!1sfr!2sfr!4v1710000000000!5m2!1sfr!2sfr"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>

                <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-[#0F3B5F] mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>Horaires d'intervention</h3>
                  <div className="flex flex-col gap-2">
                    {[
                      { day: "Lundi – Vendredi", hours: "8h00 – 19h00" },
                      { day: "Samedi", hours: "9h00 – 17h00" },
                      { day: "Dimanche", hours: "Sur rendez-vous" },
                    ].map((h) => (
                      <div key={h.day} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{h.day}</span>
                        <span className="font-semibold text-[#0F3B5F]">{h.hours}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#0F3B5F] rounded-2xl p-6 text-white">
                  <h3 className="font-bold mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>Intervention urgente ?</h3>
                  <p className="text-white/70 text-sm mb-4">Nous pouvons intervenir le jour même sur demande.</p>
                  <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 w-full py-3 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
                    <i className="ri-phone-fill"></i> {PHONE}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
